/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { GetToDosQuery, ToDosArbeitszeiten } from '@/gql/graphql';
import { Konflikt } from '../../components/Table/ToDoTable/entities/Konflikt';
import {
  Abteilung,
  Arbeitsbereich,
} from '../../components/Table/ToDoTable/entities/Arbeitsbereich';
import { Zuschlag } from '../../components/Table/ToDoTable/entities/Zuschlag';
import { Mitarbeiter } from '../../components/Table/ToDoTable/entities/Mitarbeiter';
import { Arbeitszeit } from '../../components/Table/ToDoTable/entities/Arbeitszeit';
import { Zeitstempel } from '../../components/Table/ToDoTable/entities/Zeitstempel';
import dayjs from 'dayjs';
import { Abwesenheit } from '../../components/Table/ToDoTable/entities/Abwesenheit';
import { Rechtshinweis } from '../../components/Table/ToDoTable/entities/Rechtshinweis';
import { mapAbwesenheitsgrund, Pause } from '../../entities';
import {
  Termin,
  toTerminWiederholung,
} from '@/domain/arbeitszeiten/entities/Termin';

export function getToDosKonflikte({
  data,
}: {
  data: GetToDosQuery;
}): Konflikt[] {
  const queryKonflikte = data.toDos.filter(
    (todo) => todo.__typename === 'ToDosKonflikte',
  );

  const mapedKonflikte = queryKonflikte
    .map((toDoKonflikt) => {
      // damit TypeScript versteht dass es nur ToDosKonflikte sein koennen
      if (
        toDoKonflikt.__typename !== 'ToDosKonflikte' ||
        toDoKonflikt.rootArbeitszeit === undefined ||
        toDoKonflikt.rootArbeitszeit === null
      ) {
        return undefined;
      }

      let conflictRoot: Arbeitszeit | Abwesenheit | undefined;
      let conflictCause: Arbeitszeit | Abwesenheit | undefined;

      if (toDoKonflikt.rootArbeitszeit.__typename === 'ToDosAbwesenheiten') {
        conflictRoot = new Abwesenheit({
          id: toDoKonflikt.rootArbeitszeit.id,
          arbeitsbereich: new Arbeitsbereich({
            id: toDoKonflikt.rootArbeitszeit.arbeitsbereich.id,
            name: toDoKonflikt.rootArbeitszeit.arbeitsbereich.name,
            markierung: toDoKonflikt.rootArbeitszeit.arbeitsbereich.markierung,
          }),
          dauer: toDoKonflikt.rootArbeitszeit.dauer ?? 0,
          grund: mapAbwesenheitsgrund(toDoKonflikt.rootArbeitszeit.grund),
          mitarbeiter: new Mitarbeiter({
            id: toDoKonflikt.rootArbeitszeit.mitarbeiter.id,
            vorname: toDoKonflikt.rootArbeitszeit.mitarbeiter.vorname,
            nachname: toDoKonflikt.rootArbeitszeit.mitarbeiter.nachname,
            arbeitsbereich: new Arbeitsbereich({
              id: toDoKonflikt.rootArbeitszeit.arbeitsbereich.id,
              name: toDoKonflikt.rootArbeitszeit.arbeitsbereich.name,
              markierung:
                toDoKonflikt.rootArbeitszeit.arbeitsbereich.markierung,
            }),
          }),
        });
      }
      if (
        toDoKonflikt.rootArbeitszeit.__typename === 'ToDosArbeitszeiten' &&
        toDoKonflikt.rootArbeitszeit !== undefined
      ) {
        conflictRoot = createArbeitszeit(
          toDoKonflikt.rootArbeitszeit as ToDosArbeitszeiten,
        );
      }

      if (conflictRoot === undefined) {
        return undefined;
      }

      if (toDoKonflikt.cause?.__typename === 'ToDosAbwesenheiten') {
        conflictCause = new Abwesenheit({
          id: toDoKonflikt.rootArbeitszeit.id,
          arbeitsbereich: new Arbeitsbereich({
            id: toDoKonflikt.rootArbeitszeit.arbeitsbereich.id,
            name: toDoKonflikt.rootArbeitszeit.arbeitsbereich.name,
            markierung: toDoKonflikt.rootArbeitszeit.arbeitsbereich.markierung,
          }),
          dauer: toDoKonflikt.rootArbeitszeit.dauer ?? 0,
          grund: mapAbwesenheitsgrund(toDoKonflikt.cause.grund),
          mitarbeiter: new Mitarbeiter({
            id: toDoKonflikt.cause.mitarbeiter.id,
            vorname: toDoKonflikt.cause.mitarbeiter.vorname,
            nachname: toDoKonflikt.cause.mitarbeiter.nachname,
            arbeitsbereich: new Arbeitsbereich({
              id: toDoKonflikt.cause.arbeitsbereich.id,
              name: toDoKonflikt.cause.arbeitsbereich.name,
              markierung: toDoKonflikt.cause.arbeitsbereich.markierung,
            }),
          }),
        });
      }

      if (toDoKonflikt.cause?.__typename === 'ToDosArbeitszeiten') {
        conflictCause = createArbeitszeit(
          toDoKonflikt.cause as ToDosArbeitszeiten,
        );
      }

      return new Konflikt({
        arbeitszeit: conflictRoot,
        conflictCause: conflictCause ?? null,
      });
    })
    .filter((konflikt): konflikt is Konflikt => konflikt !== undefined);

  return mapedKonflikte;
}

function createArbeitszeit(az: ToDosArbeitszeiten): Arbeitszeit {
  const arbeitsbereich = new Arbeitsbereich({
    id: az.arbeitsbereich.id,
    name: az.arbeitsbereich.name,
    markierung: az.arbeitsbereich.markierung,
    abteilungen: (az.arbeitsbereich.abteilungen ?? []).map((abteilung) => {
      return new Abteilung({
        id: abteilung.id,
      });
    }),
  });

  const zuschlaege = az.zuschlaege.map((zuschlag) => {
    if (zuschlag.betrag.__typename === 'arbeitszeiten_zuschlaege_betrag') {
      return new Zuschlag({
        name: zuschlag.name,
        prozent: zuschlag.prozent,
        betrag: zuschlag.betrag.betrag,
        dauer: zuschlag.dauer,
      });
    }
    return new Zuschlag({
      name: zuschlag.name,
      prozent: zuschlag.prozent,
      dauer: zuschlag.dauer,
    });
  });

  const termin = az.termin
    ? new Termin(
        az.termin.titel ?? undefined,
        toTerminWiederholung(az.termin.wiederholung),
        az.termin.id ?? undefined,
        az.termin.bildUrl ?? undefined,
      )
    : undefined;

  return new Arbeitszeit({
    mitarbeiter: new Mitarbeiter({
      id: az.mitarbeiter.id,
      vorname: az.mitarbeiter.vorname,
      nachname: az.mitarbeiter.nachname,
      arbeitsbereich: arbeitsbereich,
      imgUrl: az.mitarbeiter.bildUrl ?? undefined,
    }),
    arbeitsbereich: arbeitsbereich,
    id: az.id,
    dauer: az.dauer ?? undefined,
    beginn: az.beginn?.zeitpunkt
      ? new Zeitstempel({
          abweichung: az.beginn.abweichung ?? undefined,
          zeitpunkt: dayjs(az.beginn.zeitpunkt),
        })
      : undefined,
    ende: az.ende?.zeitpunkt
      ? new Zeitstempel({
          abweichung: az.ende.abweichung ?? undefined,
          zeitpunkt: dayjs(az.ende.zeitpunkt),
        })
      : undefined,
    kommen: az.kommen?.zeitpunkt
      ? new Zeitstempel({
          abweichung: az.kommen.abweichung ?? undefined,
          zeitpunkt: dayjs(az.kommen.zeitpunkt),
        })
      : undefined,
    gehen: az.gehen?.zeitpunkt
      ? new Zeitstempel({
          abweichung: az.gehen.abweichung ?? undefined,
          zeitpunkt: dayjs(az.gehen.zeitpunkt),
        })
      : undefined,
    pausen: az.pausen
      ? az.pausen.map(
          (pause) =>
            new Pause({
              id: pause.id ?? undefined,
              bezahlt: pause.bezahlt,
              art: pause.art,
              dauer: pause.dauer ?? undefined,
              beginn: pause.beginn ? new Date(pause.beginn) : undefined,
              ende: pause.ende ? new Date(pause.ende) : undefined,
            }),
        )
      : [],
    bemerkung: az.bemerkung ?? undefined,
    zuschlaege: zuschlaege,
    termin,
    rechtshinweise: az.rechtshinweise.map((rh) => {
      return new Rechtshinweis({
        name: rh.name,
        art: rh.art,
        prioritaet: rh.backgroundcolor,
      });
    }),
  });
}
